import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, LanguageVariants, Text } from 'components/common/form';

import type { FormState } from 'informed';
import type { UserCustomAttribute } from 'shared-types';

type FormValues = {
  key: string;
  label: string;
  labelTransations: { [key: string]: string };
};

interface Props {
  attribute: UserCustomAttribute;
  optionGroupIndex: number;
  optionIndex: number;
  updateData: () => Promise<void>;
}

const UserAttributesOptionsEdit = ({
  attribute,
  optionGroupIndex,
  optionIndex,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();
  const { language, messages } = useReduxState();

  const optionGroup = attribute.optionGroups[optionGroupIndex];

  const option = optionGroup.options[optionIndex];

  const existingLabels = {};
  optionGroup.options.forEach((option, index) => {
    if (index !== optionIndex) existingLabels[option.label] = true;
  });

  const handleSubmit = async (values) => {
    let options = [
      ...optionGroup.options.slice(0, optionIndex),
      {
        ...option,
        label: values.label.trim(),
        labelTranslations: values.labelTranslations
      },
      ...optionGroup.options.slice(optionIndex + 1)
    ];

    if (optionGroup.sort === 'alphabetical') {
      options = options.sort((a, b) =>
        a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
      );
    }

    const data = {
      optionGroups: [
        ...attribute.optionGroups.slice(0, optionGroupIndex),
        { ...optionGroup, options },
        ...attribute.optionGroups.slice(optionGroupIndex + 1)
      ]
    };

    await api.put(`/admin/user-custom-attributes/${attribute.id}`, data);

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.commonChangesSavesSuccessfully,
        type: 'success'
      })
    );
  };

  const validateLabel = (value) => {
    if (!value?.trim()) return messages.commonTitleEmpty;

    return existingLabels[value.trim()] ? messages.commonTitleInUse : undefined;
  };

  const initialValues = {
    key: option.key,
    label: option.label,
    labelTranslations: option.labelTranslations
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="pen"
      buttonIsDropdown={true}
      buttonSize="header"
      buttonText={messages.commonEdit}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.commonEditOption}
      modalWidth={optionGroup.enableKeys ? 'medium' : undefined}
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {({ values }: FormState<FormValues>) => (
        <div className="option">
          <div className="label">
            <FormGroup label={messages.commonTitle}>
              <Text
                field="label"
                infoMessage={
                  optionGroup.enableKeys
                    ? undefined
                    : `${
                        optionGroup.customKeyNameTranslations?.[language] ||
                        optionGroup?.customKeyName ||
                        messages.attributesKey
                      }: ${values?.key || ''}`
                }
                validate={validateLabel}
                validateOnChange={true}
              />

              {optionGroup.enableTranslations ? (
                <LanguageVariants field="labelTranslations" type="text" />
              ) : null}
            </FormGroup>
          </div>

          <div
            className="key"
            style={{ display: optionGroup.enableKeys ? 'block' : 'none' }}
          >
            <FormGroup
              label={
                optionGroup.customKeyNameTranslations?.[language] ||
                optionGroup?.customKeyName ||
                messages.attributesKey
              }
            >
              <Text disabled={true} field="key" />
            </FormGroup>
          </div>

          <style jsx>{`
            .option {
              display: flex;
              gap: 10px;
            }
            .label {
              flex-grow: 1;
            }
            .key {
              flex-grow: 1;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesOptionsEdit;
